import type { GenSourceKey } from '~/constants/generationSource'

/**
 * HatsuKomaData is one 30-minute koma row for the dashboard chart
 * (`BgChart`). The breakdown fields are keyed by `GenSourceKey` — the HJKS 9
 * 区分 (conventions.md「発電形式」節、#7537) — so the field set always matches
 * `GENERATION_SOURCE_LIST` (single SoT, `~/constants/generationSource`).
 *
 * 旧ファイル名 `generateMockData.ts` はデモ用データ生成関数 (`generateBgData` /
 * `sourceMix`) を持っていた名残り。production から呼ばれていなかったため
 * 削除し、残った production 用の型 + `summarizeBg` に合わせて
 * `hatsuKoma.ts` へ改名した (2026-09-11 是正)。
 */
export type HatsuKomaData = {
  time: string
  genPlan: number
  genActual: number | null
} & Record<GenSourceKey, number | null>

export type BgSummary = {
  /**
   * 実績が1コマでもあるか (#7695)。false の日 (未来日等) は max/min/avgUtilPct
   * が全て 0 埋めの意味の無い値になる。「発電しなかった」「計画どおり」と
   * 誤読されないよう、描画側はこのフラグで値を「—」に差し替える
   * (8080 `js/hatsu-data.js:130` の `hasActual` に対応)。
   */
  hasActual: boolean
  max: number
  maxTime: string
  min: number
  minTime: string
  avgUtilPct: number
}

export function summarizeBg(
  data: HatsuKomaData[],
  capacity: number,
): BgSummary {
  const actuals = data.filter((d) => d.genActual !== null)
  if (actuals.length === 0) {
    return {
      hasActual: false,
      max: 0,
      maxTime: '-',
      min: 0,
      minTime: '-',
      avgUtilPct: 0,
    }
  }
  const maxPt = actuals.reduce((a, b) =>
    (b.genActual ?? 0) > (a.genActual ?? 0) ? b : a,
  )
  const minPt = actuals.reduce((a, b) =>
    (b.genActual ?? 0) < (a.genActual ?? 0) ? b : a,
  )
  const avg =
    actuals.reduce((s, d) => s + (d.genActual ?? 0), 0) / actuals.length
  const utilPct = capacity > 0 ? (avg / capacity) * 100 : 0
  return {
    hasActual: true,
    max: maxPt.genActual ?? 0,
    maxTime: maxPt.time,
    min: minPt.genActual ?? 0,
    minTime: minPt.time,
    avgUtilPct: utilPct,
  }
}
